import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/redux/hooks";
import {
  getMostRecentDateSecure,
  getClassVsComponentDRPQuoteSecure,
  downloadQuoteResultAsPDF,
} from "../../../features/DRPActions";
import { setCoverageLevel } from "../../../features/DRPSlice";
import "./ResultTableClassVsComponent.scss";
import {
  DRPQuoteParams,
  StateUSA,
  SalesEffectiveDate,
} from "@agdeveloper/domain";
import CoverageSelectionDropdown from "../CoverageSelectionDropdown/CoverageSelectionDropdown";
import { _getValidPriceValue } from "../utils/ResultTableUtilityFunctions";

const coverage_levels = [
  0.95, 0.9, 0.85, 0.8,
  // 0.75,
  // 0.70,
];

const toUSFormat = (val: Number) => {
  return val.toLocaleString("en-US");
};

const toUSCurrency = (val: Number) => {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const ResultTableClassVsComponent = ({ flag, printResult }) => {
  const dispatch = useAppDispatch();
  const {
    salesEffectiveDate, // sales_effective_date
    coverageType, // pricing_type
    currentState, // state_name
    selectedQuarter, // quarter
    quarterProduction, // declared_milk_production
    classWeighting, // class_based_weighting_factor
    componentWeighting, // declared_protein_test, declared_butterfat_test, component_price_weighting_factor
    protectionFactor, // protection_factor
    drpQuoteResults,
    drpInputsChanged,

    quoteDownload,
  } = useAppSelector((state) => state.drp);

  const { estimatedIndemnity, estimatedEndorsementValue, indexValue } =
    useAppSelector((state) => state.drp.classVsComponent);

  const [selectedCoverage, setSelectedCoverage] =
    useState<string>("95% coverage");

  const handleSubmit = (level: number) => {
    setSelectedCoverage(`${level * 100}% coverage`);
    dispatch(setCoverageLevel(level));

    const drp_quote_params = new DRPQuoteParams(
      selectedQuarter, //qtr
      new StateUSA(currentState), //StateUSA
      coverageType, //quote_type we dont care fo class vs component
      parseInt(quarterProduction.replace(/\D/g, "")), //production
      classWeighting.values.class3 / 100, //class price weighting factor
      Number(componentWeighting.values.cmp_price_wf), //component price weighting factor
      Number(componentWeighting.values.butterfat), //butterfat
      Number(componentWeighting.values.protein), //protein
      Number(protectionFactor.value), //protection factor
      level, //coverage level
      1.0, //share
      0, //beginning farmer
      new SalesEffectiveDate(salesEffectiveDate.replace(/\D/g, "")), //sales effective date
    );
    // TODO: Add error handling for sending DRP Quote request
    dispatch(getClassVsComponentDRPQuoteSecure(drp_quote_params));
  };

  const getMarketConditions = () => {
    const market_conditions = {};

    market_conditions["ProjectedClass3Price"] = _getValidPriceValue(
      quoteDownload.projectedPrices.projectedClass3Price,
    );
    market_conditions["ProjectedClass4Price"] = _getValidPriceValue(
      quoteDownload.projectedPrices.projectedClass4Price,
    );
    market_conditions["ProjectedButterPrice"] = _getValidPriceValue(
      quoteDownload.projectedPrices.projectedButterPrice,
    );
    market_conditions["ProjectedProteinPrice"] = _getValidPriceValue(
      quoteDownload.projectedPrices.projectedProteinPrice,
    );
    market_conditions["ProjectedNonfatSolidsPrice"] = _getValidPriceValue(
      Math.round(
        quoteDownload.projectedPrices.projectedNonfatSolidsPrice * 10000,
      ) / 10000,
    );
    market_conditions["ProjectedOtherSolidsPrice"] = _getValidPriceValue(
      quoteDownload.projectedPrices.projectedOthersolidsPrice,
    );
    market_conditions["YieldAdjust"] = _getValidPriceValue(
      quoteDownload.projectedPrices.yieldAdjustFactor,
    );
    return market_conditions;
  };

  const downloadQuote = async () => {
    const quoteres = {
      CLPROP: {
        DeclaredCoveredMilkProduction:
          drpQuoteResults.data.CLPROP.quote_params
            .declared_covered_milk_production,
        CoverageLevelPercent:
          drpQuoteResults.data.CLPROP.quote_params.coverage_level_percent.value,
        ProtectionFactor:
          drpQuoteResults.data.CLPROP.quote_params.protection_factor,
        ProducerPremiumAmount:
          drpQuoteResults.data.CLPROP.producer_premium_amount,
        Indemnity: estimatedIndemnity["CLPROP"],
        EndorsementValue: estimatedEndorsementValue["CLPROP"],
        ExpectedRevenueAmount:
          drpQuoteResults.data.CLPROP.expected_revenue_amount,
        CoverageType:
          drpQuoteResults.data.CLPROP.quote_params.quote_type.value === "CLPROP"
            ? "CLASS"
            : "COMPONENT",
        ClassPriceWeightingFactor:
          drpQuoteResults.data.CLPROP.quote_params
            .declared_class_price_weighting_factor,
        ComponentPriceWeightingFactor:
          drpQuoteResults.data.CLPROP.quote_params
            .declared_component_price_weighting_factor,
        DeclaredButterfatTest:
          drpQuoteResults.data.CLPROP.quote_params.declared_butterfat_test,
        DeclaredProteinTest:
          drpQuoteResults.data.CLPROP.quote_params.declared_protein_test,
        Quarter: drpQuoteResults.data.CLPROP.quote_params.quarter,
        StateName: drpQuoteResults.data.CLPROP.quote_params.state_name.name,
        SalesEffectiveDate:
          drpQuoteResults.data.CLPROP.quote_params.sales_effective_date.value.toUTCString(),
      },
      CMPROP: {
        DeclaredCoveredMilkProduction:
          drpQuoteResults.data.CMPROP.quote_params
            .declared_covered_milk_production,
        CoverageLevelPercent:
          drpQuoteResults.data.CMPROP.quote_params.coverage_level_percent.value,
        ProtectionFactor:
          drpQuoteResults.data.CMPROP.quote_params.protection_factor,
        ProducerPremiumAmount:
          drpQuoteResults.data.CMPROP.producer_premium_amount,
        Indemnity: estimatedIndemnity["CMPROP"],
        EndorsementValue: estimatedEndorsementValue["CMPROP"],
        ExpectedRevenueAmount:
          drpQuoteResults.data.CMPROP.expected_revenue_amount,
        CoverageType:
          drpQuoteResults.data.CMPROP.quote_params.quote_type.value === "CLPROP"
            ? "CLASS"
            : "COMPONENT",
        ClassPriceWeightingFactor:
          drpQuoteResults.data.CMPROP.quote_params
            .declared_class_price_weighting_factor,
        ComponentPriceWeightingFactor:
          drpQuoteResults.data.CMPROP.quote_params
            .declared_component_price_weighting_factor,
        DeclaredButterfatTest:
          drpQuoteResults.data.CMPROP.quote_params.declared_butterfat_test,
        DeclaredProteinTest:
          drpQuoteResults.data.CMPROP.quote_params.declared_protein_test,
        Quarter: drpQuoteResults.data.CMPROP.quote_params.quarter,
        StateName: drpQuoteResults.data.CMPROP.quote_params.state_name.name,
        SalesEffectiveDate:
          drpQuoteResults.data.CMPROP.quote_params.sales_effective_date.value.toUTCString(),
      },
    };
    const market_conditions = getMarketConditions();
    const quote_type =
      drpQuoteResults.data.CLPROP["quote_params"]["quote_type"]["value"];
    const download_data = {
      quoteres: { ...quoteres },
      market_conditions: { ...market_conditions },
      coverage_type: quote_type,
      tool_in_use: "compare",
    };

    dispatch(downloadQuoteResultAsPDF(download_data));
  };

  useEffect(() => {
    dispatch(getMostRecentDateSecure(""));
  }, [dispatch]);

  return (
    <div className="card-body">
      <div className="side-by-side">
        <p className="card-text"></p>
        <div className="hidden">
          <span>
            <i className="bx bx-printer"></i>
          </span>
          <span
            className="fake-a-tag d-print-none"
            style={{ verticalAlign: "top" }}
          >
            Print This Quote
          </span>
        </div>
      </div>
      <div className="side-by-side">
        <p className="card-title h5">Quote Results</p>
        <div
          className={`side-by-side mb-1 ${
            !drpQuoteResults.data || drpQuoteResults.loading ? "hidden" : ""
          }`}
        >
          <span className="me-1">
            <i className="bx bx-sm bxs-printer"></i>
          </span>
          <span className="fake-a-tag d-print-none" onClick={printResult}>
            Print This Quote
          </span>
        </div>
      </div>
      <div className="side-by-side">
        <p></p>
        <div
          className={`side-by-side mb-1 ${
            !drpQuoteResults.data || drpQuoteResults.loading ? "hidden" : ""
          }`}
        >
          <span className="me-1">
            {quoteDownload.loading && <i className="bx bx-sm bx-refresh"></i>}
            {!quoteDownload.loading && quoteDownload.error && (
              <i className="bx bx-sm  bxs-error-alt"></i>
            )}

            {!quoteDownload.loading && !quoteDownload.error && (
              <i className="bx bx-sm bxs-cloud-download"></i>
            )}
          </span>
          <span className="fake-a-tag d-print-none" onClick={downloadQuote}>
            Download Quote as PDF
          </span>
        </div>
      </div>
      <div
        className="pb-1 d-flex flex-row justify-content-between"
        style={{ minHeight: "50px" }}
      >
        <CoverageSelectionDropdown
          drpInputsChanged={drpInputsChanged}
          drpQuoteResults={drpQuoteResults}
          salesEffectiveDate={salesEffectiveDate}
          selectedCoverage={selectedCoverage}
          coverage_levels={coverage_levels}
          handleSubmit={handleSubmit}
        />
        <div>
          {/* TODO: format date */}
          <span>
            Sales effective date:{" "}
            <span style={{ fontWeight: "bold" }}>{salesEffectiveDate}</span>
          </span>
        </div>
      </div>
      <div>
        <hr />
        {!drpQuoteResults.loading && drpQuoteResults.data ? (
          <div className="table-responsive">
            <table className="table table-striped mb-4">
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col">CLASS</th>
                  <th scope="col">COMPONENT</th>
                </tr>
              </thead>
              <tbody>
                {/* TODO: Format values */}
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Quarter:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "quarter"
                        ]}
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "quarter"
                        ]}
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    State:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "state_name"
                        ]["name"]}
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "state_name"
                        ]["name"]}
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Declared Milk Production:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        toUSFormat(
                          drpQuoteResults.data["CLPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ],
                        )}{" "}
                      lbs./qtr
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        toUSFormat(
                          drpQuoteResults.data["CMPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ],
                        )}{" "}
                      lbs./qtr
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Protected Milk Production:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        toUSFormat(
                          drpQuoteResults.data["CLPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ] *
                            drpQuoteResults.data["CLPROP"]["quote_params"][
                              "protection_factor"
                            ],
                        )}{" "}
                      lbs./qtr
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        toUSFormat(
                          drpQuoteResults.data["CMPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ] *
                            drpQuoteResults.data["CMPROP"]["quote_params"][
                              "protection_factor"
                            ],
                        )}{" "}
                      lbs./qtr
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Coverage Type:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "coverage_level_percent"
                        ]["value"] * 100}
                      % <span> / </span>
                      CLASS
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "coverage_level_percent"
                        ]["value"] * 100}
                      % <span> / </span>
                      COMPONENT
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Protection Factor:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "protection_factor"
                        ]}
                      x
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "protection_factor"
                        ]}
                      x
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Class Price Weighting Factor:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "declared_class_price_weighting_factor"
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>-</label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Component Price Weighting Factor:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>-</label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "declared_component_price_weighting_factor"
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Declared Butterfat Test:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "declared_butterfat_test"
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "declared_butterfat_test"
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </label>
                  </td>
                </tr>

                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Declared Protein Test:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CLPROP"]["quote_params"][
                          "declared_protein_test"
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label>
                      {drpQuoteResults.data &&
                        drpQuoteResults.data["CMPROP"]["quote_params"][
                          "declared_protein_test"
                        ].toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Index Trigger Price:
                  </th>
                  <td>
                    <label style={{ color: "#1f67bd", fontWeight: "bold" }}>
                      $
                      {drpQuoteResults.data &&
                        (
                          (drpQuoteResults.data["CLPROP"][
                            "expected_revenue_amount"
                          ] *
                            drpQuoteResults.data["CLPROP"]["quote_params"][
                              "coverage_level_percent"
                            ]["value"]) /
                          (drpQuoteResults.data["CLPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ] /
                            100.0)
                        ).toFixed(2)}
                    </label>
                  </td>
                  <td>
                    <label style={{ color: "#9a0015", fontWeight: "bold" }}>
                      $
                      {drpQuoteResults.data &&
                        (
                          (drpQuoteResults.data["CMPROP"][
                            "expected_revenue_amount"
                          ] *
                            drpQuoteResults.data["CMPROP"]["quote_params"][
                              "coverage_level_percent"
                            ]["value"]) /
                          (drpQuoteResults.data["CMPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ] /
                            100.0)
                        ).toFixed(2)}
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Producer Premium:
                  </th>
                  <td>
                    <label>
                      <span>
                        {drpQuoteResults.data &&
                          toUSCurrency(
                            drpQuoteResults.data["CLPROP"][
                              "producer_premium_amount"
                            ],
                          )}
                      </span>
                      <span className="ms-2">
                        ($
                        {drpQuoteResults.data &&
                          (
                            drpQuoteResults.data["CLPROP"][
                              "producer_premium_amount"
                            ] /
                            (drpQuoteResults.data["CLPROP"]["quote_params"][
                              "declared_covered_milk_production"
                            ] /
                              100.0)
                          ).toFixed(2)}
                        /cwt.)
                      </span>
                    </label>
                  </td>
                  <td>
                    <label>
                      <span>
                        {drpQuoteResults.data &&
                          toUSCurrency(
                            drpQuoteResults.data["CMPROP"][
                              "producer_premium_amount"
                            ],
                          )}
                      </span>
                      <span className="ms-2">
                        ($
                        {drpQuoteResults.data &&
                          (
                            drpQuoteResults.data["CMPROP"][
                              "producer_premium_amount"
                            ] /
                            (drpQuoteResults.data["CMPROP"]["quote_params"][
                              "declared_covered_milk_production"
                            ] /
                              100.0)
                          ).toFixed(2)}
                        /cwt.)
                      </span>
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Estimated Indemnity:
                  </th>
                  <td>
                    <label>
                      <span>{`$${estimatedIndemnity[
                        "CLPROP"
                      ].toLocaleString()}.00`}</span>
                      <span className="ms-2">{`($${
                        drpQuoteResults.data &&
                        (
                          estimatedIndemnity["CLPROP"] /
                          (drpQuoteResults.data["CLPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ] /
                            100.0)
                        ).toFixed(2)
                      }/cwt.) `}</span>
                    </label>
                  </td>
                  <td>
                    <label>
                      <span>{`$${estimatedIndemnity[
                        "CMPROP"
                      ].toLocaleString()}.00`}</span>
                      <span className="ms-2">{`($${
                        drpQuoteResults.data &&
                        (
                          estimatedIndemnity["CMPROP"] /
                          (drpQuoteResults.data["CMPROP"]["quote_params"][
                            "declared_covered_milk_production"
                          ] /
                            100.0)
                        ).toFixed(2)
                      }/cwt.) `}</span>
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Estimated Endorsement Value:
                    <br />
                    <span className="small fw-normal">
                      (Indemnity - Producer Premium)
                    </span>
                  </th>
                  <td>
                    <label
                      className={`${
                        // Set color based on if val is +ve/-ve
                        estimatedEndorsementValue["CLPROP"] > 0
                          ? "positive_val"
                          : "negative_val"
                      }`}
                    >
                      {estimatedEndorsementValue["CLPROP"] > 0 ? (
                        <span>
                          {`$${Math.abs(
                            estimatedEndorsementValue["CLPROP"],
                          ).toLocaleString()}.00`}
                        </span>
                      ) : (
                        // Add paranthesis if val is -ve
                        <span>
                          {`$(${Math.abs(
                            estimatedEndorsementValue["CLPROP"],
                          ).toLocaleString()}.00)`}
                        </span>
                      )}
                      <span className="ms-2">{`($${
                        drpQuoteResults.data &&
                        Math.abs(
                          estimatedEndorsementValue["CLPROP"] /
                            (drpQuoteResults.data["CLPROP"]["quote_params"][
                              "declared_covered_milk_production"
                            ] /
                              100.0),
                        ).toFixed(2)
                      }/cwt.)`}</span>
                    </label>
                  </td>
                  <td>
                    <label
                      className={`${
                        // Set color based on if val is +ve/-ve
                        estimatedEndorsementValue["CMPROP"] > 0
                          ? "positive_val"
                          : "negative_val"
                      }`}
                    >
                      {estimatedEndorsementValue["CMPROP"] > 0 ? (
                        <span>
                          {`$${Math.abs(
                            estimatedEndorsementValue["CMPROP"],
                          ).toLocaleString()}.00`}
                        </span>
                      ) : (
                        // Add paranthesis if val is -ve
                        <span>
                          {`$(${Math.abs(
                            estimatedEndorsementValue["CMPROP"],
                          ).toLocaleString()}.00)`}
                        </span>
                      )}
                      <span className="ms-2">{`($${
                        drpQuoteResults.data &&
                        Math.abs(
                          estimatedEndorsementValue["CMPROP"] /
                            (drpQuoteResults.data["CMPROP"]["quote_params"][
                              "declared_covered_milk_production"
                            ] /
                              100.0),
                        ).toFixed(2)
                      }/cwt.)`}</span>
                    </label>
                  </td>
                </tr>
                <tr>
                  <th scope="row" style={{ width: "50%" }}>
                    Index Price:
                  </th>
                  <td style={{ width: "25%" }}>
                    <label style={{ color: "#1f67bd", fontWeight: "bold" }}>
                      {indexValue["CLPROP"].toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </label>
                  </td>
                  <td style={{ width: "25%" }}>
                    <label style={{ color: "#9a0015", fontWeight: "bold" }}>
                      {indexValue["CMPROP"].toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
            {/* Disclaimer */}
            <div>
              <p
                style={{
                  fontStyle: "italic",
                  fontWeight: "normal",
                  fontSize: "0.93em",
                }}
              >
                ***NOTICE: The premium listed above is an estimate and is
                provided for reference only. Please contact your insurance agent
                for an official premium quote.
              </p>
            </div>
          </div>
        ) : drpQuoteResults.loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status"></div>
          </div>
        ) : drpQuoteResults.error ? (
          <section
            id="error-message"
            className="my-4 alert alert-danger d-flex align-items-center"
          >
            {/* <use xlinkHref="#exclamation-triangle-fill" /> */}
            <i
              className="bx bxs-error mx-2"
              style={{ color: "#842029", fontSize: "1.5rem" }}
            ></i>
            <span className="fw-bold" style={{ color: "rgb(142, 39, 54)" }}>
              {drpQuoteResults.message}
            </span>
          </section>
        ) : (
          <b>No results to display.</b>
        )}
      </div>
    </div>
  );
};

export default ResultTableClassVsComponent;
